import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const WORDMARK_URL =
  "https://customer-assets.emergentagent.com/job_merai-future/artifacts/iffhv94g_Asset%208%403x.png";

const NAV_LINKS = [
  { label: "Products", id: "products" },
  { label: "Why MERAI", id: "why-merai" },
  { label: "Contact", id: "contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#E8380D]/96 backdrop-blur-sm border-b border-black/15 shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button
            data-testid="navbar-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="cursor-pointer flex items-center"
            aria-label="MERAI Home"
          >
            <img
              src={WORDMARK_URL}
              alt="MERAI"
              className="h-9 md:h-11 w-auto object-contain"
            />
          </button>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map(({ label, id }) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => scrollTo(id)}
                className="font-mono text-xs font-bold uppercase tracking-[0.15em] text-black/75 hover:text-black transition-colors duration-200"
              >
                {label}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <button
            data-testid="navbar-book-demo-btn"
            onClick={() => scrollTo("contact")}
            className="hidden md:block bg-[#0A0A0A] text-[#00FF66] font-mono font-bold uppercase text-xs tracking-widest px-6 py-3 hover:bg-[#00FF66] hover:text-[#0A0A0A] transition-colors duration-200"
          >
            BOOK A DEMO
          </button>

          {/* Mobile Hamburger */}
          <button
            data-testid="navbar-mobile-menu-btn"
            className="md:hidden text-[#0A0A0A] p-1"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 left-0 right-0 z-40 bg-[#E8380D] border-b border-black/20 px-6 pt-6 pb-8 flex flex-col gap-5 md:hidden"
          >
            {NAV_LINKS.map(({ label, id }) => (
              <button
                key={id}
                data-testid={`mobile-nav-${id}`}
                onClick={() => scrollTo(id)}
                className="font-mono text-base font-bold uppercase tracking-widest text-[#0A0A0A] text-left border-b border-black/10 pb-5"
              >
                {label}
              </button>
            ))}
            <button
              data-testid="mobile-book-demo-btn"
              onClick={() => scrollTo("contact")}
              className="bg-[#0A0A0A] text-[#00FF66] font-mono font-bold uppercase text-sm tracking-widest py-4 w-full text-center mt-2"
            >
              BOOK A DEMO
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
